import { GetServerSidePropsContext } from "next";
import Head from "next/head";
import { useEffect, useState } from "react";

import { addToCartEvent } from "@/businessLogics/_common/pixel/landing";
import { loginRequiredSSR } from "@/businessLogics/_common/next/validate";
import LandingPageLnbMolecule from "@/components/molecules/LandingPageMolecules/LandingPageLnbMolecule";
import LandingPageBreakthroughOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageBreakthroughOrganism/LandingPageBreakthroughOrganism";
import LandingPageSellingPointOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageSellingPointOrganism";
import LandingPageSolutionOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageSolutionOrganism";
import LandingPagePartnerOrganism from "@/components/organisms/LandingPageOrganisms/LandingPagePartnerOrganism";
import Curriculums from "../src/pages/Curriculums";
import LandingPageLifeOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageLifeOrganism";
import LandingPageRecruitOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageRecruitOrganism";
import LandingPagePriceOrganism from "@/components/organisms/LandingPageOrganisms/LandingPagePriceOrganism";
import FloatingCtaMolecule from "@/components/molecules/LandingPageMolecules/FloatingCtaMolecule";
import { Footer } from "@/components/organisms/Footer";
import {
  BusinessInfoItems,
  CopyRightText,
  FooterInfoFirstLineItems,
  FooterInfoSecondLineItems,
  FooterNavItems,
  FooterSNSItems,
  InfoDropDownIconImg,
  InfoDropDownText,
} from "@/components/organisms/Footer/footer.models";
import ChannelTalkBtn from "@/components/atoms/ChannelTalkBtn";
import { logClickMktLandingButton } from "@/businessLogics/_common/logging";
import LandingPageHeroOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageHeroOrganism";
import {
  mktCTAText,
  mktHeroImage,
  mktSummaryData,
  MktTitleData,
} from "src/models/landing/hero/hero.model";
import MktLandingPageTemplate from "@/components/templates/MktLandingPageTemplate";
import MktLandingPageConcernsOraganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageConcernsOraganism/MktLandingPageConcernsOraganism";
import {
  MktDesc,
  MktTitle,
} from "src/models/landing/breakthrough/breakghrough.model";
import {
  MktSellingPointTitle,
  mktSellingPointData,
} from "src/models/landing/sellingPoint/sellingPoint.model";
import MktLandingPageBenefitsOrganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageBenefitsOrganism/MktLandingPageBenefitsOrganism";
import {
  MktSolutionTitle,
  mktLandingSolutionData,
} from "src/models/landing/solution/solution.model";

import MktLandingPageBrandsOrganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageBrandsOrganism";
import MktLandingPageSeniorsOrganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageSeniorsOrganism/MktLandingPageSeniorsOrganism";
import { MktEndingTitle } from "src/models/landing/ending/ending.model";
import LandingPageResponsibilityOrganism from "@/components/organisms/LandingPageOrganisms/LandingPageResponsibilityOrganism";
import { getApplicant } from "@/api/apply/apply";
import { courseName } from "src/models/apply";
import { useRemoteConfig } from "@/businessLogics/useRemoteConfig";
import { checkApplicant } from "@/businessLogics/applicant";
import MktLandingPageKnowHowOraganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageKnowHowOraganism";
import MktLandingPagePortfolioOraganism from "@/components/organisms/LandingPageOrganisms/MktLandingPagePortfolioOraganism";
import MktLandingPageMentorOraganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageMentorOraganism";
import MktLandingPageMarketersOrganism from "@/components/organisms/LandingPageOrganisms/MktLandingPageMarketersOrganism/MktLandingPageMarketersOrganism";
import { Navigate } from "@/components/atoms/Navigator/Navigator";
import { getPriceInfo } from "@/api/price/price";
import { Price } from "@/api/price/price.dto";

const Home = ({
  notEnrollableApplicant,
  userInfo,
  course,
  priceInfo,
}: {
  notEnrollableApplicant: { round: string; course: string } | null;
  userInfo: UserInfo;
  course: string | null;
  priceInfo: Price;
}) => {
  const [isLinkBlock, setIsLinkBlock] = useState(false);
  const { isRemoteConfigLoaded, parsedParamterValue } =
    useRemoteConfig("swc_floating_text");

  useEffect(() => {
    if (notEnrollableApplicant) {
      setIsLinkBlock(true);
    }
  }, []);

  function ctaHandler() {
    if (notEnrollableApplicant) {
      logClickMktLandingButton();
      alert(
        ` ${notEnrollableApplicant.course} ${notEnrollableApplicant.round} 지원 이력이 있습니다.\n오른쪽 아래 [1:1 문의]를 통해 담당자에게 문의해주세요.`
      );
      return;
    }

    if (!notEnrollableApplicant && course && course !== "mkt") {
      alert(
        `${courseName[course]}코스 지원 이력이 있습니다. ${courseName[course]}코스 페이지로 이동합니다.`
      );
    }

    logClickMktLandingButton();
    addToCartEvent("마케터");
  }

  const isClosed = true;
  if (isClosed) {
    return <Navigate link="/" />;
  }

  return (
    <>
      <Head>
        <title>SW캠프 | 직장병행 IT취업 부트캠프</title>
        <meta
          name="description"
          content="IT직무 커리어 전환은 SW캠프에서! 직장과 병행하며 꿈꾸던 커리어를 실무 그대로 경험해보세요."
        />
        <meta property="og:title" content="SW캠프 | 직장병행 IT취업 부트캠프" />
        <meta
          property="og:description"
          content="IT 채용 시장에서 꼭 필요한 인재가 되는 마케터 교육! 스파르타에서 새로운 커리어를 시작하세요."
        />
        <meta
          property="og:image"
          content={`${process.env.CDN_BASE_URL}/swcamp/mkt_og_image.png`}
        />
        <meta property="og:site_name" content="SW캠프 마케터 코스" />
        <meta
          name="keywords"
          content="PM, PM교육, PM강의, PM부트캠프 스파르타코딩클럽, 서비스기획자, PO, 서비스기획교육, 데이터, 개발, PM코딩, PM채용, PM취업, PO취업, 서비스기획자 취업, 프로덕트매니저, 프로덕트오너, 프로젝트매니저, 기획자, productmanager, 프로덕트 매니지먼트, 프로젝트 매니지먼트, 피그마, 사업기획, PM직무, 그로스해킹, 애자일 "
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://swcamp.spartacodingclub.kr/mkt"
        />
        <meta
          name="naver-site-verification"
          content="825fd698b7789dcdb41d72934bc879036f336c73"
        />
      </Head>

      <MktLandingPageTemplate
        lnb={<LandingPageLnbMolecule userInfo={userInfo} />}
        hero={
          <LandingPageHeroOrganism
            course="mkt"
            titleData={MktTitleData}
            heroImage={mktHeroImage}
            ctaHandler={ctaHandler}
            ctaText={mktCTAText}
          />
        }
        marketers={<MktLandingPageMarketersOrganism />}
        concerns={<MktLandingPageConcernsOraganism />}
        breakthrough={
          <LandingPageBreakthroughOrganism
            title={<MktTitle />}
            desc={<MktDesc />}
            course="mkt"
          />
        }
        benefits={<MktLandingPageBenefitsOrganism />}
        knowHow={<MktLandingPageKnowHowOraganism />}
        portfolio={<MktLandingPagePortfolioOraganism />}
        mentor={<MktLandingPageMentorOraganism />}
        sellingPoint={
          <LandingPageSellingPointOrganism
            title={<MktSellingPointTitle />}
            sellingPointData={mktSellingPointData}
            course="mkt"
          />
        }
        solution={
          <LandingPageSolutionOrganism
            title={<MktSolutionTitle />}
            landingSolutionData={mktLandingSolutionData}
          />
        }
        seniors={<MktLandingPageSeniorsOrganism />}
        curriculum={<Curriculums course="mkt" />}
        responsibility={<LandingPageResponsibilityOrganism course="mkt" />}
        brands={<MktLandingPageBrandsOrganism />}
        partners={<LandingPagePartnerOrganism />}
        recruit={<LandingPageRecruitOrganism course="mkt" />}
        price={<LandingPagePriceOrganism course="mkt" price={priceInfo} />}
        life={
          <LandingPageLifeOrganism
            course="mkt"
            title={<MktEndingTitle />}
            ctaText="마케터 코스 지원하기"
            ctaHandler={ctaHandler}
          />
        }
        footer={
          <Footer
            footerNavItems={FooterNavItems}
            footerSNSItems={FooterSNSItems}
            footerInfoFirstLineItems={FooterInfoFirstLineItems}
            footerInfoSecondLineItems={FooterInfoSecondLineItems}
            businessInfoItems={BusinessInfoItems}
            infoDropDownText={InfoDropDownText}
            infoDropDownIconImg={InfoDropDownIconImg}
            copyRightText={CopyRightText}
          />
        }
        // floatingCta={
        //   <FloatingCtaMolecule
        //     course="mkt"
        //     ctaHandler={ctaHandler}
        //     isAlimSeason={false}
        //     isRemoteConfigLoaded={isRemoteConfigLoaded}
        //     parsedParamterValue={parsedParamterValue}
        //   />
        // }
      />
      <ChannelTalkBtn />
    </>
  );
};

export const getServerSideProps = async ({
  req,
  res,
  resolvedUrl,
}: GetServerSidePropsContext) => {
  const [userInfo] = loginRequiredSSR(req, res, resolvedUrl);
  const priceInfo = await getPriceInfo("마케터코스");

  let course = null;
  let applicant = null;
  let notEnrollableApplicant = null;
  if (userInfo) {
    try {
      applicant = await getApplicant(userInfo._id);
      course = applicant.round.courseType;
      notEnrollableApplicant = await checkApplicant(
        applicant,
        notEnrollableApplicant
      );
    } catch (e) {
      console.error(e);
    }
  }

  return {
    props: {
      notEnrollableApplicant,
      course,
      userInfo,
      priceInfo,
    },
  };
};

export default Home;
